import { camelizeKeys } from "humps";
import { equals, pipe } from "ramda";

import { CARD_API_URL, LOYALTY_API_URL } from "api_urls";
import {
  checkStatus,
  deleteRequest,
  getRequest,
  parseJSON,
  postRequest,
} from "helpers/ajax_request";
import getEnv from "helpers/get_env";
import formatCards from "packages/loyalty/api/helpers/credit_cards_transform";

function getCreditCardInfo(multipleTokens = false) {
  if (equals(getEnv(), "development")) {
    const localData = multipleTokens
      ? import("packages/loyalty/local_data/cards_multiple_tokens.json")
      : import("packages/loyalty/local_data/cards.json");
    return localData.then(CARDS => {
      return pipe(camelizeKeys, formatCards)(CARDS.default);
    });
  }

  return fetch(`${LOYALTY_API_URL}/user/cards`, getRequest())
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys)
    .then(formatCards);
}

function postCreditCard(cardWithToken, callback, errorCallback) {
  return fetch(`${CARD_API_URL}/consumers/card`, postRequest(cardWithToken))
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys)
    .then(formatCards)
    .then(callback)
    .catch(errorCallback);
}

function postTokenRequestForNewCard(
  callback,
  userUuid,
  enrollmentSalt,
  enrollmentSig
) {
  let fetchOptions;
  if (userUuid)
    fetchOptions = postRequest({ userUuid, enrollmentSalt, enrollmentSig });
  else fetchOptions = postRequest();

  return fetch(`${LOYALTY_API_URL}/card_enrollment_token`, fetchOptions)
    .then(checkStatus)
    .then(parseJSON)
    .then(callback);
}

async function deleteCreditCard(card) {
  if (equals(getEnv(), "development")) return;

  const failedTokens = [];

  for (const token of card.tokens) {
    try {
      await fetch(
        `${LOYALTY_API_URL}/user/cards/${token}`,
        deleteRequest(card)
      ).then(checkStatus);
    } catch (error) {
      failedTokens.push(token);
    }
  }

  if (failedTokens.length > 0) {
    throw new Error(
      `Failed to delete ${failedTokens.length} of ${card.tokens.length} token(s)`
    );
  }
}

async function saveLoyaltyCreditCard(card, email) {
  const tokenResponse = await fetch(
    `${LOYALTY_API_URL}/card_enrollment_token`,
    postRequest()
  )
    .then(checkStatus)
    .then(parseJSON);

  const cardWithToken = {
    cardEnrollmentToken: tokenResponse.token,
    unencryptedPan: card.cardNumber,
    cardholderName: `${card.firstName} ${card.lastName}`,
    expirationMonth: card.expiryMonth,
    expirationYear: card.expiryYear,
    customerEmail: email,
  };

  return fetch(`${CARD_API_URL}/consumers/card`, postRequest(cardWithToken))
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys)
    .then(formatCards);
}

export {
  postCreditCard,
  deleteCreditCard,
  getCreditCardInfo,
  postTokenRequestForNewCard,
  saveLoyaltyCreditCard,
};
